import React from "react";
import { Link } from "react-router-dom";
import { CONTACT_INFO } from "../data";
import { ArrowUpRight, Shield, Award, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export default function Footer() {
  const { lang, t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Language-aware links
  const footerLinks = [
    { name: t("nav.home"), path: lang === "da" ? "/" : "/en" },
    { name: t("nav.services"), path: lang === "da" ? "/ydelser" : "/en/services" },
    { name: t("nav.projects"), path: lang === "da" ? "/projekter" : "/en/projects" },
    { name: t("nav.about"), path: lang === "da" ? "/om-os" : "/en/about" },
    { name: t("nav.contact"), path: lang === "da" ? "/kontakt" : "/en/contact" },
  ];

  return (
    <footer className="bg-stone-100 text-stone-900 border-t border-stone-200 pt-20 pb-12">
      <div className="w-full px-6 md:px-12 lg:px-16">
        {/* Top Section: Large Branding and Statement */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-stone-200">
          <div className="lg:col-span-6 flex flex-col justify-between">
            <div>
              <span className="text-2xl md:text-3xl font-light tracking-[0.25em] uppercase">
                CPH <span className="font-semibold">Collective</span>
              </span>
              <p className="mt-6 max-w-md text-stone-600 text-sm md:text-base font-light leading-relaxed">
                {t("footer.desc")}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-stone-500 text-xs font-mono">
              <span className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-stone-600" />
                {t("footer.insured")}
              </span>
              <span className="flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-stone-600" />
                {t("footer.craftsmanship")}
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-stone-600" />
                {t("footer.delivery")}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono mb-6">
              {t("footer.nav")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-stone-600 hover:text-stone-900 transition-colors duration-300 flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono mb-6">
              {t("footer.hq")}
            </h4>
            <address className="not-italic space-y-3 text-sm text-stone-600">
              <p className="leading-relaxed">{CONTACT_INFO.address}</p>
              <p className="font-mono text-xs pt-2">
                T:{" "}
                <a
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="hover:text-stone-900 transition-colors"
                >
                  {CONTACT_INFO.phone}
                </a>
              </p>
              <p className="font-mono text-xs">
                E:{" "}
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="hover:text-stone-900 transition-colors"
                >
                  {CONTACT_INFO.email}
                </a>
              </p>
              <p className="font-mono text-xs">CVR: {CONTACT_INFO.cvr}</p>
            </address>
          </div>
        </div>

        {/* Bottom Section: Copyright and Back to Top */}
        <div className="pt-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-xs text-stone-500 font-mono">
            <span>© {currentYear} CPH Collective. {t("footer.rights")}</span>
            <span className="hidden sm:inline text-stone-300">•</span>
            <div className="flex items-center gap-3">
              <a
                href="https://www.iubenda.com/privacy-policy/91409364"
                className="iubenda-white iubenda-noiframe iubenda-embed hover:text-stone-900 transition-colors"
                title="Privatlivspolitik "
              >
                Privatlivspolitik
              </a>
              <span className="text-stone-300">•</span>
              <a
                href="https://www.iubenda.com/privacy-policy/91409364/cookie-policy"
                className="iubenda-white iubenda-noiframe iubenda-embed hover:text-stone-900 transition-colors"
                title="Cookiepolitik "
              >
                Cookiepolitik
              </a>
            </div>
          </div>
          <button
            onClick={handleScrollToTop}
            className="text-xs uppercase tracking-[0.25em] text-stone-600 hover:text-stone-900 transition-colors duration-300 font-mono flex items-center gap-2 group cursor-pointer"
          >
            {t("footer.top")}
            <span className="inline-block transition-transform duration-300 group-hover:-translate-y-1">
              ↑
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
